/**
 * Hazard severity tiering (PRD §7, P0 Area Scan).
 *
 * Three tiers, in escalating order. The wording is the technician's, not a
 * model's: a finding is something you either note, slow down for, or refuse to
 * work under.
 *
 * Severity is NEVER communicated by colour alone. Every tier carries an icon and
 * a text label in both clients - roughly 8% of this workforce has a red/green
 * colour deficiency, and a hazard list that depends on hue is a hazard list some
 * of them cannot read.
 */

export const SEVERITY_TIERS = ["advisory", "caution", "stop"] as const;

export type SeverityTier = (typeof SEVERITY_TIERS)[number];

export interface SeverityMeta {
  /** Shown on the chip. */
  label: string;
  /** One line, shown in the legend and in the finding detail sheet. */
  description: string;
  /** Design-token family. Resolves to `--color-{token}` / `--color-{token}-bg`. */
  token: "advisory" | "caution" | "stop";
  /** Lucide icon name (web) / Material icon name (Flutter). Keep the two in step. */
  icon: string;
  /** Ascending order of seriousness - safe to sort on. */
  rank: number;
}

export const SEVERITY_META: Record<SeverityTier, SeverityMeta> = {
  advisory: {
    label: "Advisory",
    description: "Worth knowing about. Does not change how the task is done.",
    token: "advisory",
    icon: "info",
    rank: 1,
  },
  caution: {
    label: "Caution",
    description: "Needs a control in place before work starts.",
    token: "caution",
    icon: "triangle-alert",
    rank: 2,
  },
  stop: {
    label: "Stop",
    description: "Do not proceed. Escalate to a supervisor.",
    token: "stop",
    icon: "octagon-x",
    rank: 3,
  },
};

export function isSeverityTier(value: unknown): value is SeverityTier {
  return typeof value === "string" && (SEVERITY_TIERS as readonly string[]).includes(value);
}

/** Descending - the worst finding first, which is the order the list renders in. */
export function compareSeverityDesc(a: SeverityTier, b: SeverityTier): number {
  return SEVERITY_META[b].rank - SEVERITY_META[a].rank;
}
